"use client";

import { useEffect, useId, useRef, useState, type KeyboardEvent } from "react";
import { useRouter } from "next/navigation";
import { cx } from "@/components/cx";
import { Icon, Check, ChevronDown } from "@/components/icon";
import { useProUpsell } from "@/components/pro-modal-provider";
import { SHARE_DESCRIPTION_MAX, type ShareVisibility } from "@/lib/contracts/share";
import type { OwnerShare } from "@/lib/maps/ownerShare";
import type { QuotaLimit } from "@/lib/billing/quota";
import { quotaLimitToTrigger } from "@/lib/analytics/paywall";

/**
 * The share & embed panel (FAIL-425, redesigned MAP-119/D4) on the map detail
 * page. Owner-scoped: reads the map's live share, lets the owner set
 * visibility through an accessible dropdown listbox, and copy the share link
 * + `<iframe>` embed snippet.
 *
 * MAP-119: Public and private drafts are free; Unlisted publishing is Pro-only
 * (D3's server-side 402 gate on the PATCH route). Gated options stay fully
 * actionable for free accounts — selecting one opens the generic Pro modal
 * (`useProUpsell`, D2) instead of disabling the control. The "Password
 * protection" row is a pure upsell decoy: `share_configs` has no password
 * column in this slice, so nothing the field does ever persists, for either
 * plan — focusing/clicking it just opens the same modal for free accounts.
 *
 * No shared Menu/listbox primitive exists to reuse here: `src/components/popover.tsx`
 * is a plain anchored-panel shell (no keyboard handling, hardcodes `role="dialog"`,
 * no ref/keydown passthrough) built for simple content like StylePanel's color
 * picker, not listbox semantics; `AccountMenu`/`pz-mapcard__menu` establish this
 * codebase's actual convention for a real dropdown — a local `open` state, a
 * `document` mousedown listener for click-outside, and hand-rolled a11y wiring
 * per consumer. This component follows that same convention.
 */

const VISIBILITIES: Array<{ value: ShareVisibility; label: string; hint: string }> = [
  { value: "public", label: "Public", hint: "Listed and discoverable." },
  { value: "unlisted", label: "Unlisted", hint: "Anyone with the link." },
  { value: "private", label: "Private", hint: "Only you can open it." },
];

/** Unlisted publishing requires Pro; Private is the free unpublished-draft state. */
export function isGatedVisibility(v: ShareVisibility): boolean {
  return v === "unlisted";
}

export type VisibilitySelection =
  | { kind: "gated" }
  | { kind: "noop" }
  | { kind: "commit"; visibility: ShareVisibility };

/**
 * Pure decision for what selecting `next` should do (MAP-119). Free accounts
 * can only ever commit `public` — picking Unlisted/Private opens the Pro
 * modal and never reaches the PATCH. Re-selecting the value already in effect
 * is a no-op (skips a pointless PATCH) rather than gating or re-sending it.
 * Extracted as pure logic so it's unit-testable without a real DOM (this
 * project's tests run in the `node` vitest environment — no
 * jsdom/testing-library — see pro-modal-provider.test.ts / modal.tsx's
 * `trapFocusIndex` for the same pattern).
 */
export function resolveVisibilitySelection(
  next: ShareVisibility,
  current: ShareVisibility,
  isPaid: boolean,
): VisibilitySelection {
  // Check the no-op case FIRST: re-selecting the value already in effect must
  // never gate, even for a free account whose map is grandfathered at a
  // Pro-only value (D3's server-side idempotency — resending the SAME
  // already-stored gated value is an allowed no-op, never a 402 — applies
  // client-side too, so this never opens the modal for a reselection that
  // changes nothing).
  if (next === current) return { kind: "noop" };
  if (isGatedVisibility(next) && !isPaid) return { kind: "gated" };
  return { kind: "commit", visibility: next };
}

/** Pure roving-highlight math for the listbox's Arrow key navigation — wraps at both ends. */
export function nextVisibilityHighlight(current: number, direction: 1 | -1, length: number): number {
  return (current + direction + length) % length;
}

export function SharePanel({
  mapId,
  share,
  isPaid,
}: {
  mapId: string;
  share: OwnerShare | null;
  /** Tier-derived (FAIL-413) — Unlisted/Private visibility + password are Pro. */
  isPaid: boolean;
}) {
  const router = useRouter();
  const { openProUpsell } = useProUpsell();
  const [visibility, setVisibility] = useState<ShareVisibility>(share?.visibility ?? "private");
  const [busy, setBusy] = useState(false);
  const [description, setDescription] = useState(share?.description ?? "");
  const [savedDescription, setSavedDescription] = useState(share?.description ?? "");
  const [descBusy, setDescBusy] = useState(false);
  const [descError, setDescError] = useState(false);

  const [menuOpen, setMenuOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listboxRef = useRef<HTMLDivElement>(null);
  const listboxId = useId();

  // Focus the listbox on open + close on outside click (same pattern as
  // AccountMenu / the map-card action menu — this codebase's established
  // hand-rolled-dropdown convention; see the file header).
  useEffect(() => {
    if (!menuOpen) return;
    listboxRef.current?.focus();
    function onDocMouseDown(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setMenuOpen(false);
    }
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [menuOpen]);

  function openMenu() {
    const idx = VISIBILITIES.findIndex((v) => v.value === visibility);
    setHighlight(idx === -1 ? 0 : idx);
    setMenuOpen(true);
  }

  function closeMenu(returnFocus: boolean) {
    setMenuOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  }

  async function changeVisibility(next: ShareVisibility) {
    const prev = visibility;
    setVisibility(next);
    setBusy(true);
    try {
      const res = await fetch(`/api/account/maps/${mapId}/share`, {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ visibility: next }),
      });
      if (res.status === 402) {
        // Defensive: a stale client attempted a gated PATCH the server (D3)
        // rejects. Revert the optimistic change and open the same Pro modal
        // a client-side gate would have — never leave the UI showing a value
        // that was never actually saved.
        setVisibility(prev);
        openProUpsell({ trigger: "private_share" });
        return;
      }
      if (res.status === 409) {
        const body = (await res.json().catch(() => null)) as {
          status?: string;
          limit_hit?: QuotaLimit;
        } | null;
        setVisibility(prev);
        if (body?.status === "quota_exceeded" && body.limit_hit) {
          openProUpsell({ trigger: quotaLimitToTrigger(body.limit_hit) });
          return;
        }
      }
      if (!res.ok) throw new Error();
      router.refresh();
    } catch {
      setVisibility(prev); // revert on failure
    } finally {
      setBusy(false);
    }
  }

  function selectVisibility(next: ShareVisibility) {
    if (busy) return;
    closeMenu(true);
    const outcome = resolveVisibilitySelection(next, visibility, isPaid);
    if (outcome.kind === "gated") {
      openProUpsell({ trigger: "private_share" });
      return;
    }
    if (outcome.kind === "noop") return;
    void changeVisibility(outcome.visibility);
  }

  function onTriggerClick() {
    if (menuOpen) closeMenu(false);
    else openMenu();
  }

  function onTriggerKeyDown(e: KeyboardEvent<HTMLButtonElement>) {
    if (menuOpen) return;
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      openMenu();
    }
  }

  function onListboxKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlight((i) => nextVisibilityHighlight(i, 1, VISIBILITIES.length));
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlight((i) => nextVisibilityHighlight(i, -1, VISIBILITIES.length));
        break;
      case "Enter":
      case " ":
        e.preventDefault();
        selectVisibility(VISIBILITIES[highlight]!.value);
        break;
      case "Escape":
        e.preventDefault();
        closeMenu(true);
        break;
      case "Tab":
        closeMenu(false);
        break;
      default:
        break;
    }
  }

  /** The password row is a decoy for every plan (no backend exists yet) — for
   *  free accounts, focusing or clicking it opens the Pro modal; Pro accounts
   *  already see no PRO badge/gate here (nothing to upsell them into). */
  function openPasswordUpsell() {
    if (!isPaid) openProUpsell({ trigger: "share_password" });
  }

  async function saveDescription() {
    setDescBusy(true);
    setDescError(false);
    try {
      const res = await fetch(`/api/account/maps/${mapId}/share`, {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ description }),
      });
      if (!res.ok) throw new Error();
      setSavedDescription(description);
      router.refresh();
    } catch {
      setDescError(true);
    } finally {
      setDescBusy(false);
    }
  }

  if (!share) {
    return (
      <section className="pz-share" aria-label="Share">
        <h2 className="pz-share__title">Share &amp; embed</h2>
        <p className="pz-share__empty">
          This map isn’t shared yet. Open it in the editor and publish it to get a link and an embed.
        </p>
      </section>
    );
  }

  const current = VISIBILITIES.find((v) => v.value === visibility) ?? VISIBILITIES[0]!;

  return (
    <section className="pz-share" aria-label="Share">
      <h2 className="pz-share__title">Sharing</h2>

      <span className="pz-share__vislabel">Visibility</span>
      <div className="pz-share__visibility" ref={containerRef}>
        <button
          type="button"
          ref={triggerRef}
          className="pz-share__vistrigger"
          aria-haspopup="listbox"
          aria-expanded={menuOpen}
          aria-controls={listboxId}
          aria-label={`Visibility: ${current.label}`}
          disabled={busy}
          onClick={onTriggerClick}
          onKeyDown={onTriggerKeyDown}
        >
          <span aria-hidden>{current.label}</span>
          <Icon icon={ChevronDown} size="sm" className="pz-share__vischev" />
        </button>
        <div
          id={listboxId}
          ref={listboxRef}
          role="listbox"
          aria-label="Visibility options"
          aria-activedescendant={`${listboxId}-opt-${VISIBILITIES[highlight]!.value}`}
          tabIndex={-1}
          hidden={!menuOpen}
          className="pz-share__vislistbox"
          onKeyDown={onListboxKeyDown}
        >
          {VISIBILITIES.map((v, i) => {
            const gated = isGatedVisibility(v.value) && !isPaid;
            return (
              <div
                key={v.value}
                id={`${listboxId}-opt-${v.value}`}
                role="option"
                aria-selected={visibility === v.value}
                aria-label={gated ? `${v.label} (Pro feature)` : undefined}
                className={cx(
                  "pz-share__visopt",
                  highlight === i && "pz-share__visopt--active",
                  visibility === v.value && "pz-share__visopt--selected",
                )}
                onMouseEnter={() => setHighlight(i)}
                onClick={() => selectVisibility(v.value)}
              >
                <span className="pz-share__visopt-main">
                  <span className="pz-share__visopt-name">{v.label}</span>
                  <span className="pz-share__visopt-hint">{v.hint}</span>
                </span>
                <span className="pz-share__visopt-indicators">
                  {gated ? <span className="pz-share__probadge">PRO</span> : null}
                  {visibility === v.value ? (
                    <Icon icon={Check} size="sm" className="pz-share__visopt-check" />
                  ) : null}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {!isPaid ? (
        <p className="pz-share__seonote">Drafts stay private. Publish when your map uses features included in your plan.</p>
      ) : null}

      {/* The link + embed live in the header "Share" action (MapShareButton) —
          a copyable link on demand at the top, not a full URL printed here at
          the bottom. This panel keeps the sharing *settings* only. */}
      {visibility === "private" ? (
        <p className="pz-share__hint">Make it unlisted or public to get a shareable link and embed code.</p>
      ) : null}

      <div className="pz-share__prorow">
        <div className="pz-share__prorow-top">
          <span className="pz-share__prorow-label">Password protect your map</span>
          {!isPaid ? <span className="pz-share__probadge">PRO</span> : null}
        </div>
        <input
          type="text"
          className="pz-share__prorow-field"
          placeholder="Add a password"
          readOnly
          value=""
          aria-label={!isPaid ? "Password protect your map (Pro feature)" : "Password protect your map"}
          onFocus={openPasswordUpsell}
          onClick={openPasswordUpsell}
        />
        <p className="pz-share__prorow-sub">Require a password to open this map. Included in Pro.</p>
      </div>

      {visibility !== "private" ? (
        <div className="pz-share__field">
          <label className="pz-share__fieldlabel" htmlFor="pz-share-description">
            Description
          </label>
          <textarea
            id="pz-share-description"
            className="pz-share__description"
            value={description}
            maxLength={SHARE_DESCRIPTION_MAX}
            placeholder="What's this map about? Shown on the public map page and used as its search-result snippet."
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
          />
          <div className="pz-share__descriptionrow">
            <span className="pz-share__hint">
              {description.length}/{SHARE_DESCRIPTION_MAX} characters
              {descError ? " — couldn't save, try again" : ""}
            </span>
            <button
              type="button"
              className="pz-btn pz-btn--secondary pz-btn--sm"
              disabled={descBusy || description === savedDescription}
              onClick={() => void saveDescription()}
            >
              {descBusy ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
}
